import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';

export type DropdownSmartInsightsPropertyFilterOption = Option & {
  hotelName: string;
  hotelinfoId: string;
  id: string;
  value: string;
};

interface MarketSmartInsightsDropdownsDropdownPropertyFilterSignature {
  Args: {
    isLoading: boolean;
    selectedPropertyIds: string[];
    setSelectedPropertyIds: (ids: string[]) => void;
  };
}

export default class MarketSmartInsightsDropdownsDropdownPropertyFilter extends Component<MarketSmartInsightsDropdownsDropdownPropertyFilterSignature> {
  @service declare intl: Services['intl'];
  @service declare portfolioSmartInsights: Services['portfolio-smart-insights'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get options(): DropdownSmartInsightsPropertyFilterOption[] {
    const options = this.portfolioSmartInsights.filteredSmartInsightsHotels.map(
      (hotel) => {
        // Hotels that aren't on the performance plan can't be added to the view.
        // Disable them and explain why via the option's own tooltip.
        const isUpsell = !hotel.hasSiMpv;

        return {
          label: hotel.hotelName,
          value: hotel.hotelinfoId,
          id: hotel.hotelinfoId,
          hotelinfoId: hotel.hotelinfoId,
          hotelName: hotel.hotelName,
          disabled: isUpsell,
          disabledMessage: isUpsell
            ? this.intl.t(
                'market.smart_insights.filter.property.upsell_tooltip',
              )
            : undefined,
        };
      },
    );

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }

  // Only selectable properties count towards the total: disabled (upsell) hotels
  // can never be added to the view.
  @cached
  get selectablePropertiesCount(): number {
    return this.options.filter((option) => !option.disabled).length;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedProperties() {
    const { selectedPropertyIds: appliedPropertyIds } = this.args;
    return this.options.filter((propertyOption) =>
      appliedPropertyIds.includes(propertyOption.id),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPropertiesCount() {
    return this.selectedProperties.length;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildLabelWithCount(count: number) {
    const maxCount = this.selectablePropertiesCount;

    if (maxCount === 0) {
      return this.intl.t('market.smart_insights.filter.property.placeholder');
    }

    const label = this.intl.t('market.smart_insights.filter.property.count');
    return `${count}/${maxCount} ${label}`;
  }

  @action onChange(options: DropdownSmartInsightsPropertyFilterOption[]): void {
    const sortedOptions = options.toSorted((a, b) =>
      a.label.localeCompare(b.label),
    );

    const acceptableNumberOfOptionsIds = sortedOptions.map(
      (option) => option.id,
    );

    this.args.setSelectedPropertyIds(acceptableNumberOfOptionsIds);

    this.usageAnalytics.track('Smart Insights Filtered', {
      eventTrigger: 'smart-insights-property-filter',
      product: 'smart-insights-multi-property',
      type: 'property',
      values: acceptableNumberOfOptionsIds,
    });
  }

  <template>
    <PrismComboSelectInput
      @buildLabelWithCount={{this.buildLabelWithCount}}
      @disabled={{@isLoading}}
      @disabledMessage={{if
        @isLoading
        (t "market.smart_insights.filter.loading_tooltip")
      }}
      @isSearchable={{true}}
      @onChange={{this.onChange}}
      @options={{this.options}}
      @selectAllLabel={{t "market.smart_insights.filter.property.all_selected"}}
      @value={{this.selectedProperties}}
    />
  </template>
}
